/**
 * The "deliberate switch" M1-29's AC calls for — see the customer app's
 * `lib/maintenanceFlag.ts`, which this mirrors exactly. Defaults to this
 * app's OWN same-origin file (`public/maintenance-flag.json`); a shared
 * outage naming BOTH apps would need `VITE_MAINTENANCE_FLAG_URL` pointed at
 * the same override location for each, since the two are separate static
 * sites on separate origins.
 */
export interface MaintenanceFlag {
  maintenance: boolean;
  message?: string;
}

const FLAG_URL = import.meta.env.VITE_MAINTENANCE_FLAG_URL || '/maintenance-flag.json';

const OFF: MaintenanceFlag = { maintenance: false };

export async function fetchMaintenanceFlag(): Promise<MaintenanceFlag> {
  try {
    const response = await fetch(FLAG_URL, { cache: 'no-store', credentials: 'omit' });
    if (!response.ok) {
      return OFF;
    }
    const data: unknown = await response.json();
    if (typeof data !== 'object' || data === null || !('maintenance' in data)) {
      return OFF;
    }
    const { maintenance, message } = data as { maintenance: unknown; message?: unknown };
    return {
      maintenance: maintenance === true,
      message: typeof message === 'string' ? message : undefined,
    };
  } catch {
    // No flag file, a network blip, invalid JSON — all read the same as "not
    // flagged". The file's ABSENCE is the normal case for anyone who has not
    // set up an override location, and a broken fetch here must never itself
    // manufacture an outage.
    return OFF;
  }
}
